import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDT } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Camera } from "@phosphor-icons/react";

export default function MobileReports() {
  const [mine, setMine] = useState([]);
  const [sites, setSites] = useState([]);
  const [config, setConfig] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ site_id: "", type: "Incident", severity: "low", title: "", description: "" });
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/incidents").then((r) => setMine(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/attendance/my").then((r) => {
      const seen = {};
      const list = [];
      r.data.forEach((s) => { if (s.site && !seen[s.site_id]) { seen[s.site_id] = 1; list.push({ id: s.site_id, name: s.site_name }); } });
      setSites(list);
    }).catch(() => {});
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      let photos = [];
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        photos = [data.path];
      }
      await api.post("/incidents", { ...form, photos });
      toast.success("Report submitted — supervisors notified");
      setShowForm(false);
      setForm({ site_id: "", type: "Incident", severity: "low", title: "", description: "" });
      setPhoto(null);
      load();
    } catch (e) {
      toast.error(navigator.onLine ? errMsg(e) : "You are offline — reports need a connection (attendance can be queued offline)");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4" data-testid="mobile-reports">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-muted-foreground">My reports</div>
        <Button size="sm" className="rounded-none" data-testid="new-report-button" onClick={() => setShowForm(!showForm)}>
          {showForm ? "Cancel" : "New report"}
        </Button>
      </div>

      {showForm && (
        <div className="border border-[hsl(var(--warning))] bg-card p-4 mb-4" data-testid="report-form">
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase font-bold">Site</Label>
              <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                <SelectTrigger data-testid="report-site" className="rounded-none mt-1"><SelectValue placeholder="Select site" /></SelectTrigger>
                <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase font-bold">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="report-type" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{(config?.report_types || ["Incident"]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase font-bold">Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger data-testid="report-severity" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{(config?.severities || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-[10px] uppercase font-bold">Title</Label><Input data-testid="report-title" className="rounded-none mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label className="text-[10px] uppercase font-bold">What happened</Label><Textarea data-testid="report-description" className="rounded-none mt-1 min-h-[100px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div>
              <Label className="text-[10px] uppercase font-bold flex items-center gap-1"><Camera size={14} /> Photo evidence</Label>
              <Input data-testid="report-photo" type="file" accept="image/*" capture="environment" className="rounded-none mt-1" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
            </div>
            <Button className="w-full rounded-none h-11 font-heading font-bold" data-testid="report-submit" disabled={busy || !form.site_id || !form.title || !form.description} onClick={submit}>
              {busy ? "SUBMITTING…" : "SUBMIT REPORT"}
            </Button>
          </div>
        </div>
      )}

      {mine.length === 0 && !showForm && <div className="font-mono text-xs text-muted-foreground border border-border p-6">No reports filed yet</div>}
      <div className="space-y-2">
        {mine.map((r) => (
          <div key={r.id} className="border border-border bg-card p-3" data-testid={`report-${r.ref_no}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono text-[10px] text-muted-foreground">{r.ref_no}</span>
              <div className="flex gap-1"><StatusBadge value={r.severity} /><StatusBadge value={r.status} /></div>
            </div>
            <div className="text-sm font-semibold">{r.title}</div>
            <div className="font-mono text-[10px] text-muted-foreground mt-1">{r.site_name} · {fmtDT(r.created_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
